import { useCallback, useEffect, useState } from 'react';
import parse from 'html-react-parser';
import { debounce } from 'lodash';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import { getContentByTitle } from '@/components/customercenter-ui/components/product/details/helpers/utils/productDataUtils';
import HelpIcon from '@/components/customercenter-ui/icons/help';
import { Dialog, DialogContent, DialogTrigger } from '@/components/customercenter-ui/ui/dialog';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getSelectedDivision } from '@/helpers/utils/siteUtils';

const WFD_CACHE_KEY = 'WFDModalContent';

const WorryFreeDeliveryModal = () => {
  const projectSettings: ProjectSettings = useProjSettings();
  const [open, setOpen] = useState(false);
  const [WFDContent, setWFDContent] = useState('');

  const handleTooltipClick = useCallback(
    debounce(() => {
      setOpen?.(true);
    }, 200),
    [],
  );

  const getWFDContent = (contentData: any) => {
    const shippingInsuranceContent = mapLocaleToMeaningfulFormat()?.shippingInsuranceContent;
    return contentData?.shippingInsurance_ContentEntries?.[0]?.[shippingInsuranceContent]?.content;
  };

  const WFDModalCacheTimeInMinutes = projectSettings?.WFDModalCacheTimeInMinutes ?? 15;

  const getCachedWFDContent = (entryTitle: string) => {
    try {
      const divisionCode = getSelectedDivision();
      const cache = localStorage.getItem(divisionCode + WFD_CACHE_KEY);
      if (!cache) return undefined;
      const parsed = JSON.parse(cache);
      const cachedEntry = parsed?.[entryTitle];
      if (!cachedEntry?.data || !cachedEntry?.date) return undefined;
      const cachedTime = new Date(cachedEntry.date).getTime();
      const currentTime = new Date().getTime();
      const minutesDiff = (currentTime - cachedTime) / (1000 * 60);
      return minutesDiff <= WFDModalCacheTimeInMinutes ? cachedEntry.data : undefined;
    } catch {
      return undefined;
    }
  };

  const setCachedWFDContent = (entryTitle: string, data: any) => {
    try {
      const divisionCode = getSelectedDivision();
      const cache = localStorage.getItem(divisionCode + WFD_CACHE_KEY);
      const parsed = cache ? JSON.parse(cache) : {};
      parsed[entryTitle] = {
        data,
        date: new Date().toISOString(),
      };
      localStorage.setItem(divisionCode + WFD_CACHE_KEY, JSON.stringify(parsed));
    } catch {
      console.error('Error while fetching content.');
    }
  };

  useEffect(() => {
    const fetchWorryFreeDeliveryContent = async () => {
      try {
        const entryTitle = projectSettings?.narvarInsuranceContentTitle;
        if (!entryTitle) {
          return;
        }
        let contentData: any = getCachedWFDContent(entryTitle);
        if (!contentData) {
          contentData = await getContentByTitle(entryTitle, true);
          setCachedWFDContent(entryTitle, contentData);
        }
        const WFDModalContent = getWFDContent(contentData);
        setWFDContent(WFDModalContent);
      } catch (error) {
        console.error('Error retrieving worry free delivery content:', error);
      }
    };
    fetchWorryFreeDeliveryContent();
  }, []);

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) {
          setOpen?.(false);
        }
      }}
    >
      <DialogTrigger asChild>
        <button
          type="button"
          className="inline-flex cursor-pointer"
          onClick={handleTooltipClick}
          aria-label="Info Icon"
          id="worryFreeDelivery-tooltip"
          aria-controls="worryFreeDelivery-tooltip"
        >
          <HelpIcon />
        </button>
      </DialogTrigger>
      <DialogContent
        className="
                h-[calc(100%_-_48px)]
                max-h-[786px]
                w-[calc(100%_-_30px)]
                max-w-[534px]
                overflow-hidden
                rounded-lg
                border-0
                bg-transparent
                p-0
                [&>button>svg]:h-[26px]
                [&>button>svg]:w-[26px]
                [&>button]:z-20
                [&>button]:h-[26px]
                [&>button]:w-[26px]
                [&>button]:text-white
                 "
        onOpenAutoFocus={(e) => e.preventDefault()}
        id="worryFreeDelivery-modal"
        aria-labelledby="worryFreeDelivery modal"
      >
        <section
          id="worryFreeDelivery-modal"
          data-testid="worry-free-delivery-modal"
          className="custom-scrollbar h-full w-full overflow-y-auto"
        >
          {parse(WFDContent)}
        </section>
      </DialogContent>
    </Dialog>
  );
};

export default WorryFreeDeliveryModal;
